import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalState, useOnline } from '@ndrc/app-sdk/client';
import { RichText } from '@ndrc/ui';
import { callCore, mutateCore } from '../lib/rpc';
import { useDesk } from '../desk/context';
import { t } from '../messages';
import { MissionPreviewProblem } from '../teacher/MissionPreviewProblem';
export interface ProblemList {
  items: { id: string; title: string; description: string; status: string; version: string }[];
  truncated: boolean;
}
export function MissionProblems({
  problems,
  runId,
  enabled = false,
  onResolved,
  previewClassId,
}: {
  problems?: ProblemList;
  runId: string;
  enabled?: boolean;
  onResolved?: () => Promise<void>;
  previewClassId?: string;
}) {
  if (!problems?.items.length) return null;
  return (
    <details>
      <summary>{t('mission.problems.title')}</summary>
      <p>{t('mission.problems.help')}</p>
      {problems.truncated && <p role="status">{t('mission.problems.truncated')}</p>}
      {problems.items.map((problem) => (
        <article key={problem.id}>
          <h4>{problem.title}</h4>
          <RichText text={problem.description} />
          <p>
            {t(
              problem.status === 'done'
                ? 'mission.problems.done'
                : problem.status === 'cancelled'
                  ? 'mission.problems.cancelled'
                  : 'mission.problems.open',
            )}
          </p>
          {previewClassId && problem.status === 'open' && (
            <MissionPreviewProblem
              classId={previewClassId}
              runId={runId}
              problemId={problem.id}
              version={problem.version}
              enabled={enabled}
            />
          )}
          {!previewClassId && onResolved && problem.status === 'open' && (
            <ProblemDeclaration
              problem={problem}
              runId={runId}
              enabled={enabled}
              refresh={onResolved}
            />
          )}
        </article>
      ))}
    </details>
  );
}
function ProblemDeclaration({
  problem,
  runId,
  enabled,
  refresh,
}: {
  problem: ProblemList['items'][number];
  runId: string;
  enabled: boolean;
  refresh: () => Promise<void>;
}) {
  const { me } = useDesk();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [request, setRequest, stored] = useLocalState(
    `mission-problem:${me.id}:${runId}:${problem.id}`,
    { eventId: null as string | null, requestId: crypto.randomUUID(), version: problem.version },
  );
  const receipt = useQuery({
    queryKey: ['core', 'missions.responseStatus', request.eventId],
    queryFn: () =>
      callCore<{ status: string; message?: string }>('missions.responseStatus', {
        eventId: request.eventId,
      }),
    enabled: !!request.eventId,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.status && query.state.data.status !== 'pending' ? false : 2000,
  });
  return (
    <>
      {!stored && <p role="alert">{t('teacher.missions.storageError')}</p>}
      <button
        className="ndrc-button"
        type="button"
        disabled={!enabled || !online || busy || !!request.eventId}
        onClick={() => {
          setBusy(true);
          setError('');
          void mutateCore<{ eventId: string }>(
            'missions.resolveProblem',
            {
              runId,
              requestId: request.requestId,
              problemId: problem.id,
              expectedVersion: request.version,
            },
            `problem:${request.requestId}`,
          )
            .then(async (result) => {
              setRequest({ ...request, eventId: result.eventId });
              await refresh();
            })
            .catch((e: unknown) =>
              setError(e instanceof Error ? e.message : t('teacher.automation.error')),
            )
            .finally(() => setBusy(false));
        }}
      >
        {t('mission.problems.resolve')}
      </button>
      {busy && <p role="status">{t('mission.problems.saving')}</p>}
      {request.eventId && (
        <p role="status">
          {receipt.data?.status === 'ok'
            ? t('mission.problems.done')
            : receipt.data?.status === 'rejected'
              ? receipt.data.message
              : t('mission.response.pending')}
        </p>
      )}
      {receipt.isError && <p role="alert">{t('mission.student.error')}</p>}
      {error && <p role="alert">{error}</p>}
      {(error || receipt.data?.status === 'rejected') && (
        <button
          type="button"
          className="ndrc-button"
          disabled={busy || !online}
          onClick={() => {
            setBusy(true);
            void refresh()
              .then(() => {
                setRequest({
                  eventId: null,
                  requestId: crypto.randomUUID(),
                  version: problem.version,
                });
                setError('');
              })
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : t('mission.student.error')),
              )
              .finally(() => setBusy(false));
          }}
        >
          {t('mission.response.resume')}
        </button>
      )}
    </>
  );
}
